/**
 * FileNamingService - Generates standardized filenames for all documents
 *
 * Format: LastnameF_MMDDYYYY_FormName_MMDDYYYY.pdf
 * - Lastname + First Initial
 * - Date of Birth (MMDDYYYY)
 * - Form Name (sanitized)
 * - Submission Date (MMDDYYYY)
 *
 * Example: JohnsonS_03151985_StandardIntake_11102025.pdf
 */

export interface FileNamingOptions {
  clientName: string;
  dateOfBirth: string; // ISO format or MM/DD/YYYY
  formName: string;
  submissionDate?: Date; // Defaults to now
  fileExtension?: string; // Defaults to 'pdf'
}

class FileNamingService {
  private static instance: FileNamingService;

  public static getInstance(): FileNamingService {
    if (!FileNamingService.instance) {
      FileNamingService.instance = new FileNamingService();
    }
    return FileNamingService.instance;
  }

  /**
   * Generate standardized filename for form documents
   */
  public generateFormFilename(options: FileNamingOptions): string {
    const {
      clientName,
      dateOfBirth,
      formName,
      submissionDate = new Date(),
      fileExtension = 'pdf'
    } = options;

    // Extract lastname and first initial
    const namePart = this.extractNameIdentifier(clientName);

    // Format DOB as MMDDYYYY
    const dobPart = this.formatDateToMMDDYYYY(dateOfBirth);

    // Sanitize form name
    const formPart = this.sanitizeFormName(formName);

    // Format submission date as MMDDYYYY
    const submitPart = this.formatDateToMMDDYYYY(submissionDate);

    // Combine parts
    return `${namePart}_${dobPart}_${formPart}_${submitPart}.${fileExtension}`;
  }

  /**
   * Extract lastname and first initial from full name
   * "Sarah Johnson" → "JohnsonS"
   * "Mary Jane Smith" → "SmithM"
   */
  private extractNameIdentifier(fullName: string): string {
    const nameParts = fullName.trim().split(/\s+/);

    if (nameParts.length === 0) {
      return 'Unknown';
    }

    // Get first name (first part)
    const firstName = nameParts[0];
    const firstInitial = firstName.charAt(0).toUpperCase();

    // Get last name (last part)
    const lastName = nameParts[nameParts.length - 1];
    const lastNameCapitalized = this.capitalizeFirstLetter(lastName);

    return `${lastNameCapitalized}${firstInitial}`;
  }

  /**
   * Format date to MMDDYYYY
   * Accepts: Date object, ISO string, or MM/DD/YYYY string
   */
  private formatDateToMMDDYYYY(date: string | Date): string {
    let dateObj: Date;

    if (typeof date === 'string') {
      // Try parsing as ISO format first
      if (date.includes('T') || date.includes('-')) {
        dateObj = new Date(date);
      } else if (date.includes('/')) {
        // Parse MM/DD/YYYY format
        const [month, day, year] = date.split('/');
        dateObj = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      } else {
        // Fallback: try direct parse
        dateObj = new Date(date);
      }
    } else {
      dateObj = date;
    }

    // Validate date
    if (isNaN(dateObj.getTime())) {
      console.error('Invalid date provided:', date);
      return '01011900'; // Fallback
    }

    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');
    const year = String(dateObj.getFullYear());

    return `${month}${day}${year}`;
  }

  /**
   * Sanitize form name for filename
   * "Standard Intake Questionnaire" → "StandardIntake"
   * "GAD-7 Anxiety Assessment" → "GAD7Anxiety"
   */
  private sanitizeFormName(formName: string): string {
    return formName
      .replace(/[^a-zA-Z0-9\s]/g, '') // Remove special characters
      .split(/\s+/) // Split by whitespace
      .filter(word => word.length > 0) // Remove empty strings
      .map(word => this.capitalizeFirstLetter(word)) // Capitalize each word
      .slice(0, 3) // Take first 3 words max
      .join('');
  }

  /**
   * Capitalize first letter of string
   */
  private capitalizeFirstLetter(str: string): string {
    if (!str || str.length === 0) return '';
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  }

  /**
   * Generate folder path for client documents
   * Format: {companyName}/{clientName_DOB}/
   */
  public generateClientFolderPath(
    companyName: string,
    clientName: string,
    dateOfBirth: string
  ): string {
    const sanitizedCompany = this.sanitizeCompanyName(companyName);
    const namePart = this.extractNameIdentifier(clientName);
    const dobPart = this.formatDateToMMDDYYYY(dateOfBirth);

    return `${sanitizedCompany}/${namePart}_${dobPart}`;
  }

  /**
   * Sanitize company name for folder path
   * "MyClinicCore Practice" → "mycliniccore-practice"
   */
  private sanitizeCompanyName(companyName: string): string {
    return companyName
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, '') // Remove special characters
      .trim()
      .replace(/\s+/g, '-'); // Replace spaces with hyphens
  }

  /**
   * Get full file path for form PDF
   */
  public getFormFilePath(options: FileNamingOptions & { companyName: string }): string {
    const basePath = this.generateClientFolderPath(
      options.companyName,
      options.clientName,
      options.dateOfBirth
    );
    const filename = this.generateFormFilename(options);

    return `${basePath}/forms/completed/${filename}`;
  }

  /**
   * Parse filename back to components (for display/searching)
   */
  public parseFilename(filename: string): {
    nameIdentifier: string;
    dateOfBirth: string;
    formName: string;
    submissionDate: string;
    extension: string;
  } | null {
    // Remove extension
    const filenameParts = filename.split('.');
    const extension = filenameParts.pop() || 'pdf';
    const nameWithoutExt = filenameParts.join('.');

    // Split by underscore
    const parts = nameWithoutExt.split('_');

    if (parts.length !== 4) {
      console.error('Invalid filename format:', filename);
      return null;
    }

    return {
      nameIdentifier: parts[0],
      dateOfBirth: parts[1],
      formName: parts[2],
      submissionDate: parts[3],
      extension
    };
  }

  /**
   * Format parsed DOB for display
   * "03151985" → "03/15/1985"
   */
  public formatDOBForDisplay(dobMMDDYYYY: string): string {
    if (dobMMDDYYYY.length !== 8) return dobMMDDYYYY;

    const month = dobMMDDYYYY.substring(0, 2);
    const day = dobMMDDYYYY.substring(2, 4);
    const year = dobMMDDYYYY.substring(4, 8);

    return `${month}/${day}/${year}`;
  }

  /**
   * Format date to MMDDYY (2-digit year)
   * Used for session recordings to keep filenames shorter
   */
  private formatDateToMMDDYY(date: string | Date): string {
    let dateObj: Date;

    if (typeof date === 'string') {
      if (date.includes('T') || date.includes('-')) {
        dateObj = new Date(date);
      } else if (date.includes('/')) {
        const [month, day, year] = date.split('/');
        dateObj = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      } else {
        dateObj = new Date(date);
      }
    } else {
      dateObj = date;
    }

    if (isNaN(dateObj.getTime())) {
      console.error('Invalid date provided:', date);
      return '010100';
    }

    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');
    const year = String(dateObj.getFullYear()).slice(-2); // Last 2 digits of year

    return `${month}${day}${year}`;
  }

  /**
   * Format time to HHMM AM/PM
   * Example: "1030AM", "0315PM"
   */
  private formatTimeToHHMMAMPM(date: Date): string {
    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';

    // Convert to 12-hour format
    hours = hours % 12;
    hours = hours ? hours : 12; // 0 should be 12

    const hoursStr = String(hours).padStart(2, '0');

    return `${hoursStr}${minutes}${ampm}`;
  }

  /**
   * Generate filename for session video recording
   * Format: PatientName_MMDDYY_SessionType_HHMM{AM/PM}_video.mp4
   * Example: JohnDoe_112205_Intake_1030AM_video.mp4
   */
  public generateSessionVideoFilename(options: {
    clientName: string;
    sessionDate: Date;
    sessionType: string; // e.g., "Intake", "FollowUp", "Assessment"
    fileExtension?: string;
  }): string {
    const { clientName, sessionDate, sessionType, fileExtension = 'mp4' } = options;

    // Extract name part (LastnameF format)
    const namePart = this.extractNameIdentifier(clientName);

    // Format date as MMDDYY
    const datePart = this.formatDateToMMDDYY(sessionDate);

    // Sanitize session type
    const sessionPart = this.sanitizeFormName(sessionType);

    // Format time as HHMM{AM/PM}
    const timePart = this.formatTimeToHHMMAMPM(sessionDate);

    return `${namePart}_${datePart}_${sessionPart}_${timePart}_video.${fileExtension}`;
  }

  /**
   * Generate filename for session audio recording (original)
   * Format: PatientName_MMDDYY_SessionType_HHMM{AM/PM}_audio_original.mp3
   * Example: JohnDoe_112205_Intake_1030AM_audio_original.mp3
   */
  public generateSessionAudioOriginalFilename(options: {
    clientName: string;
    sessionDate: Date;
    sessionType: string;
    fileExtension?: string;
  }): string {
    const { clientName, sessionDate, sessionType, fileExtension = 'mp3' } = options;

    const namePart = this.extractNameIdentifier(clientName);
    const datePart = this.formatDateToMMDDYY(sessionDate);
    const sessionPart = this.sanitizeFormName(sessionType);
    const timePart = this.formatTimeToHHMMAMPM(sessionDate);

    return `${namePart}_${datePart}_${sessionPart}_${timePart}_audio_original.${fileExtension}`;
  }

  /**
   * Generate filename for session audio recording (edited)
   * Format: PatientName_MMDDYY_SessionType_HHMM{AM/PM}_audio_edited.mp3
   * Example: JohnDoe_112205_Intake_1030AM_audio_edited.mp3
   */
  public generateSessionAudioEditedFilename(options: {
    clientName: string;
    sessionDate: Date;
    sessionType: string;
    fileExtension?: string;
  }): string {
    const { clientName, sessionDate, sessionType, fileExtension = 'mp3' } = options;

    const namePart = this.extractNameIdentifier(clientName);
    const datePart = this.formatDateToMMDDYY(sessionDate);
    const sessionPart = this.sanitizeFormName(sessionType);
    const timePart = this.formatTimeToHHMMAMPM(sessionDate);

    return `${namePart}_${datePart}_${sessionPart}_${timePart}_audio_edited.${fileExtension}`;
  }

  /**
   * Generate base filename for session (without file type suffix)
   * Used as common base for all session files
   * Format: PatientName_MMDDYY_SessionType_HHMM{AM/PM}
   * Example: JohnDoe_112205_Intake_1030AM
   */
  public generateSessionBaseFilename(options: {
    clientName: string;
    sessionDate: Date;
    sessionType: string;
  }): string {
    const { clientName, sessionDate, sessionType } = options;

    const namePart = this.extractNameIdentifier(clientName);
    const datePart = this.formatDateToMMDDYY(sessionDate);
    const sessionPart = this.sanitizeFormName(sessionType);
    const timePart = this.formatTimeToHHMMAMPM(sessionDate);

    return `${namePart}_${datePart}_${sessionPart}_${timePart}`;
  }
}

export default FileNamingService;
